import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useAppStore, useAuthStore } from "./store/useStore";
import { Layout } from "./components/layout/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Reservations from "./pages/Reservations";
import LibroDigital from "./pages/LibroDigital";
import FloorPlan from "./pages/FloorPlan";
import Customers from "./pages/Customers";
import Analytics from "./pages/Analytics";
import Settings from "./pages/Settings";
import WidgetBuilder from "./pages/WidgetBuilder";
import Waitlist from "./pages/Waitlist";
import WaitlistRegister from "./pages/WaitlistRegister";
import Marketing from "./pages/Marketing";
import Detection from "./pages/Detection";
import Events from "./pages/Events";
import EventPublicPage from "./pages/EventPublicPage";
import RiskDashboard from "./pages/RiskDashboard";
import LandingPage from "./pages/LandingPage";
import WidgetOccasions from "./pages/WidgetOccasions";
import EventWidgetBuilder from "./pages/EventWidgetBuilder";
import Experiences from "./pages/Experiences";
import ExperienceWidgetBuilder from "./pages/ExperienceWidgetBuilder";
import Integrations from "./pages/Integrations";
// Widget pages
import WidgetPreview from "./pages/WidgetPreview";
import EventWidgetPage from "./pages/EventWidgetPage";
// Public API Docs pages
import SwaggerDocsPage from "./pages/docs/SwaggerDocsPage";
import RedocDocsPage from "./pages/docs/RedocDocsPage";
import OpenAPIJsonPage from "./pages/docs/OpenAPIJsonPage";
// Admin pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminRestaurants from "./pages/admin/AdminRestaurants";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminAPIDocs from "./pages/admin/AdminAPIDocs";
import AdminIntegrations from "./pages/admin/AdminIntegrations";
import AdminWidgets from "./pages/admin/AdminWidgets";
import AdminBubbleIO from "./pages/admin/AdminBubbleIO";
import AdminOAuthSSO from "./pages/admin/AdminOAuthSSO";
import AdminSuperConnect from "./pages/admin/AdminSuperConnect";
import AdminOpenRouter from "./pages/admin/AdminOpenRouter";
import AdminAIAgents from "./pages/admin/AdminAIAgents";
import AdminClientes from "./pages/admin/AdminClientes";
import AdminRapidAPI from "./pages/admin/AdminRapidAPI";
import AdminLegal from "./pages/admin/AdminLegal";
import AdminStripe from "./pages/admin/AdminStripe";
import AdminMCP from "./pages/admin/AdminMCP";
import MisPlanes from "./pages/MisPlanes";
// Legal pages
import TerminosPage from "./pages/legal/TerminosPage";
import PrivacidadPage from "./pages/legal/PrivacidadPage";
import AvisoLegalPage from "./pages/legal/AvisoLegalPage";
import CookiesPage from "./pages/legal/CookiesPage";
// Client Portal pages
import ClientLogin from "./pages/clientes/ClientLogin";
import ClientChangePin from "./pages/clientes/ClientChangePin";
import ClientDashboard from "./pages/clientes/ClientDashboard";
// ===== NEW GROWTH FEATURES =====
import WhatsAppNotifications from "./pages/WhatsAppNotifications";
import PostVisitFollowup from "./pages/PostVisitFollowup";
import FeedbackNPS from "./pages/FeedbackNPS";
import Reports from "./pages/Reports";
import TableTurnTime from "./pages/TableTurnTime";
import Allergens from "./pages/Allergens";
import Deposits from "./pages/Deposits";
import GiftCards from "./pages/GiftCards";
import Memberships from "./pages/Memberships";
import BookingPage from "./pages/BookingPage";
import SocialBookings from "./pages/SocialBookings";
import LoyaltyProgram from "./pages/LoyaltyProgram";
import APIDocumentation from "./pages/APIDocumentation";
import CorporatePortal from "./pages/CorporatePortal";
import MarketBenchmark from "./pages/MarketBenchmark";
import StaffScheduling from "./pages/StaffScheduling";
import PrivateDining from "./pages/PrivateDining";
import AllergenMenuBuilder from "./pages/AllergenMenuBuilder";
import DigitalWalkIn from "./pages/DigitalWalkIn";
import CFDIPage from "./pages/CFDIPage";
import GuaranteedAccess from "./pages/GuaranteedAccess";
import MenusCartas from "./pages/MenusCartas";
import WidgetCartaExpress from "./pages/WidgetCartaExpress";
import GiftCardWidgetBuilder from "./pages/GiftCardWidgetBuilder";
import OkWallet from "./pages/OkWallet";
import AdminOkWallet from "./pages/admin/AdminOkWallet";
import AdminRetellAgents from "./pages/admin/AdminRetellAgents";
import AdminEmailServer from "./pages/admin/AdminEmailServer";
import AdminKapso from "./pages/admin/AdminKapso";
import VoiceAgent from "./pages/VoiceAgent";
import EmailMarketing from "./pages/EmailMarketing";
import EventsWidgetPublicPage from "./pages/EventsWidgetPublicPage";
import ExperiencesWidgetPublicPage from "./pages/ExperiencesWidgetPublicPage";
import GiftCardWidgetPublicPage from "./pages/GiftCardWidgetPublicPage";
import CartaWidgetPublicPage from "./pages/CartaWidgetPublicPage";
import { ErrorBoundary } from "./components/ErrorBoundary";
import "./lib/i18n";

function App() {
  const { theme } = useAppStore();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
          } />
          
          {/* Public Waitlist Registration (QR Code) */}
          <Route path="/waitlist-register/:restaurantId" element={<WaitlistRegister />} />
          
          {/* Public Event Page */}
          <Route path="/evento/:slug" element={<EventPublicPage />} />
          
          {/* Public Widget Pages */}
          <Route path="/widget/:restaurantId" element={<WidgetPreview />} />
          <Route path="/widget/:restaurantId/evento/:eventId" element={<EventWidgetPage />} />
          <Route path="/widget/events/:restaurantId" element={<EventsWidgetPublicPage />} />
          <Route path="/widget/experiences/:restaurantId" element={<ExperiencesWidgetPublicPage />} />
          <Route path="/widget/gift-cards/:restaurantId" element={<GiftCardWidgetPublicPage />} />
          <Route path="/widget/carta/:restaurantId" element={<CartaWidgetPublicPage />} />
          
          {/* Landing Page - Super Poderes */}
          <Route path="/superpoderes" element={<LandingPage />} />

          {/* Legal Pages */}
          <Route path="/terminos" element={<TerminosPage />} />
          <Route path="/privacidad" element={<PrivacidadPage />} />
          <Route path="/aviso-legal" element={<AvisoLegalPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          
          {/* Public API Documentation Routes */}
          <Route path="/docs/swagger" element={<SwaggerDocsPage />} />
          <Route path="/docs/redoc" element={<RedocDocsPage />} />
          <Route path="/docs/openapi" element={<OpenAPIJsonPage />} />

          {/* Protected Restaurant Routes */}
          <Route element={<ErrorBoundary><Layout /></ErrorBoundary>}>
            {/* Core Operations */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/libro" element={<LibroDigital />} />
            <Route path="/reservations" element={<Reservations />} />
            <Route path="/floor-plan" element={<FloorPlan />} />
            <Route path="/waitlist" element={<Waitlist />} />
            <Route path="/turn-time" element={<TableTurnTime />} />
            {/* Guests */}
            <Route path="/customers" element={<Customers />} />
            <Route path="/loyalty" element={<LoyaltyProgram />} />
            <Route path="/memberships" element={<Memberships />} />
            <Route path="/allergens" element={<Allergens />} />
            {/* Revenue */}
            <Route path="/events" element={<Events />} />
            <Route path="/experiences" element={<Experiences />} />
            <Route path="/gift-cards" element={<GiftCards />} />
            <Route path="/deposits" element={<Deposits />} />
            {/* Channels */}
            <Route path="/whatsapp" element={<WhatsAppNotifications />} />
            <Route path="/followup" element={<PostVisitFollowup />} />
            <Route path="/social-bookings" element={<SocialBookings />} />
            <Route path="/booking-page" element={<BookingPage />} />
            {/* Analytics */}
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/nps" element={<FeedbackNPS />} />
            <Route path="/reports" element={<Reports />} />
            {/* Marketing */}
            <Route path="/corporate" element={<CorporatePortal />} />
            <Route path="/benchmark" element={<MarketBenchmark />} />
            <Route path="/personal" element={<StaffScheduling />} />
            <Route path="/private-dining" element={<PrivateDining />} />
            <Route path="/allergen-menu" element={<AllergenMenuBuilder />} />
            <Route path="/walk-in" element={<DigitalWalkIn />} />
            <Route path="/cfdi" element={<CFDIPage />} />
            <Route path="/acceso-garantizado" element={<GuaranteedAccess />} />
            <Route path="/menus-cartas" element={<MenusCartas />} />
            <Route path="/widget-carta-express" element={<WidgetCartaExpress />} />
            <Route path="/mis-planes" element={<MisPlanes />} />

            <Route path="/marketing" element={<Marketing />} />
            <Route path="/email-marketing" element={<EmailMarketing />} />
            <Route path="/voice-agent" element={<VoiceAgent />} />
            <Route path="/detection" element={<Detection />} />
            <Route path="/risk-dashboard" element={<RiskDashboard />} />
            {/* Config & Tools */}
            <Route path="/settings" element={<Settings />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/widget" element={<WidgetBuilder />} />
            <Route path="/api-docs" element={<APIDocumentation />} />
            <Route path="/dashboard/widget-occasions" element={<WidgetOccasions />} />
            <Route path="/widget-config" element={<WidgetOccasions />} />
            <Route path="/event-widget" element={<EventWidgetBuilder />} />
            <Route path="/experience-widget" element={<ExperienceWidgetBuilder />} />
            <Route path="/gift-card-widget" element={<GiftCardWidgetBuilder />} />
            <Route path="/ok-wallet" element={<OkWallet />} />
          </Route>

          {/* Super Admin Routes */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route element={<AdminLayout />}>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/restaurants" element={<AdminRestaurants />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/payments" element={<AdminPayments />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/integrations" element={<AdminIntegrations />} />
            <Route path="/admin/widgets" element={<AdminWidgets />} />
            <Route path="/admin/api-docs" element={<AdminAPIDocs />} />
            <Route path="/admin/bubble" element={<AdminBubbleIO />} />
            <Route path="/admin/oauth-sso" element={<AdminOAuthSSO />} />
            <Route path="/admin/super-connect" element={<AdminSuperConnect />} />
            <Route path="/admin/openrouter" element={<AdminOpenRouter />} />
            <Route path="/admin/ai-agents" element={<AdminAIAgents />} />
            <Route path="/admin/clientes" element={<AdminClientes />} />
            <Route path="/admin/rapidapi" element={<AdminRapidAPI />} />
            <Route path="/admin/legal" element={<AdminLegal />} />
            <Route path="/admin/stripe" element={<AdminStripe />} />
            <Route path="/admin/mcp" element={<AdminMCP />} />
            <Route path="/admin/ok-wallet" element={<AdminOkWallet />} />
            <Route path="/admin/retell" element={<AdminRetellAgents />} />
            <Route path="/admin/email-server" element={<AdminEmailServer />} />
            <Route path="/admin/kapso" element={<AdminKapso />} />
          </Route>

          {/* Client Portal Routes */}
          <Route path="/clientes" element={<ClientLogin />} />
          <Route path="/clientes/:nickname" element={<ClientLogin />} />
          <Route path="/clientes/:nickname/cambiar-pin" element={<ClientChangePin />} />
          <Route path="/clientes/:nickname/dashboard" element={<ClientDashboard />} />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      
      <Toaster 
        position="top-right" 
        richColors 
        closeButton
        toastOptions={{
          style: {
            background: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            color: 'hsl(var(--foreground))',
          },
        }}
      />
    </div>
  );
}

export default App;
